<template>
    <section class="landing-hero">
        <div class="landing-hero__container">
            <h1>
                <span class="landing-hero__title-lead">Your cottage food business</span><span class="landing-hero__comma">,</span
                ><br class="landing-hero__title-break" /><span class="landing-underline">clear and connected</span>
            </h1>
            <p>
                Bring the core parts of your business into one system, so everything stays connected: recipes, supplies, products, orders, customers,
                costs, and more.
            </p>
            <p class="landing-hero-unlock">
                Unlock more time to focus on your
                <TypingTaglineIsland />
            </p>

            <HeroEarlyAccessIsland />

            <div class="landing-hero__screenshots">
                <LandingScreenshots :screenshots="props.screenshots" :theme="props.theme" embedded />
            </div>
        </div>
    </section>
</template>

<script lang="ts" setup>
    import type { LandingTheme } from '@/pages/Landing/composables/useLandingTheme';
    import HeroEarlyAccessIsland from '@/pages/Landing/islands/HeroEarlyAccessIsland.vue';
    import TypingTaglineIsland from '@/pages/Landing/islands/TypingTaglineIsland.vue';
    import type { LandingScreenshot } from '@/pages/Landing/landingScreenshots';
    import LandingScreenshots from './LandingScreenshots.vue';

    const props = defineProps<{
        screenshots: LandingScreenshot[];
        theme: LandingTheme;
    }>();
</script>

<style lang="scss" scoped>
    .landing-hero {
        padding-top: 10em;
        padding-bottom: 80px;

        &__container {
            max-width: 1400px;
            margin: 0 auto;
            padding: 0 16px;
        }

        h1 {
            color: var(--landing-text);
            margin: 0 auto;
            font-size: 3em;
            font-weight: 700;
            text-align: center;
        }

        p {
            margin: 16px auto 0;
            max-width: 720px;
            font-size: 18px;
            line-height: 1.55;
            color: var(--landing-muted);
            text-align: center;
        }

        p:first-of-type {
            color: var(--landing-muted);
            font-size: 1.4em;
        }

        p.landing-hero-unlock {
            color: var(--landing-text);
            font-weight: 600;
        }

        &__screenshots {
            margin: 4vh auto 0;
        }

        &__comma {
            &::after {
                content: ' ';
            }
        }

        &__title-break {
            display: none;
        }
    }

    @media (max-width: 600px) {
        .landing-hero {
            padding-top: 108px;
            padding-bottom: 48px;

            h1 {
                font-size: 1.8rem;
                line-height: 1.15;
            }

            &__screenshots {
                margin: 16px auto 0;
            }

            &__comma {
                display: none;
            }

            &__title-break {
                display: block;
            }
        }
    }

    :global(html.has-landing[data-landing-theme='light']) {
        .landing-hero {
            h1 {
                color: #444444;
            }

            p:first-of-type {
                color: #666666;
            }

            p.landing-hero-unlock {
                color: #777777;
            }
        }
    }

    .landing-underline {
        background-image: linear-gradient(var(--landing-accent), var(--landing-accent));
        background-repeat: no-repeat;
        background-position: 0 100%;
        background-size: 100% 0.14em;
        padding-bottom: 0.06em;
    }
</style>
